/**
 * W1-11b (app-shell-ui): the editor route `#/p/<projectId>/<workspace>` (DESIGN.md §3.1, §4.4): banners, the top
 * bar, the workspace and the hint bar around the open project's DocStore. While the project session opens the
 * project (reload, Back, a link) it shows "Opening the chart..."; when opening failed, why, with "Try again" and
 * "Back to the Library". A missing or unknown workspace segment is corrected to the Chart workspace.
 * W3-01 (UX-016): the reason is said in words per storage failure (another tab, deleted, storage full or
 * unreadable), never with the chart's id or storage terms.
 */

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { ProjectId } from '../../core/model/types';
import { DocStoreProvider } from '../../state/react';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { ProgressBar } from '../../ui/ProgressBar';
import { isShellNavService, isWorkspaceId } from '../navService';
import type { AppServices } from '../registry/types';
import type { ShellUiService } from '../uiService';
import { Banners } from './Banners';
import { errorText } from './download';
import { persistErrorCode } from '../projectSession';
import { m as sessionMessages } from '../session.messages';
import { HintBar } from './HintBar';
import { useEditorState, useStore, useText } from './ShellContext';
import { TopBar } from './TopBar';
import { WorkspaceLayout } from './WorkspaceLayout';
import { m } from './shell.messages';
import s from './Shell.module.css';

const noop = (): void => undefined;

function useNavState(services: AppServices): { readonly opening: ProjectId | null; readonly error: unknown } {
  const nav = services.nav;
  const shellNav = isShellNavService(nav) ? nav : null;
  const subscribe = useCallback(
    (cb: () => void): (() => void) => (shellNav === null ? noop : shellNav.subscribe(cb)),
    [shellNav],
  );
  const getOpening = useCallback(() => shellNav?.opening ?? null, [shellNav]);
  const getError = useCallback(() => shellNav?.openError ?? null, [shellNav]);
  const opening = useSyncExternalStore(subscribe, getOpening, getOpening);
  const error = useSyncExternalStore(subscribe, getError, getError);
  return { opening, error };
}

export function EditorShell(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly params: Readonly<Record<string, string>>;
}) {
  const { services, ui, params } = props;
  const text = useText(services);
  const store = useStore(services);
  const workspace = useEditorState(services, (st) => st.workspace);
  const id = params.id ?? '';
  const wsParam = params.workspace;
  const { opening, error } = useNavState(services);

  // Normalise the workspace segment (missing or unknown -> current / chart).
  useEffect(() => {
    if (!isWorkspaceId(wsParam)) services.nav.setWorkspace(isWorkspaceId(workspace) ? workspace : 'chart');
  }, [wsParam, workspace, services]);

  if (store?.projectId !== id) {
    const failed = error !== null && opening === null;
    return (
      <div className={s.editorWaiting} aria-busy={failed ? undefined : true}>
        {failed ? (
          <EmptyState
            icon="FileWarning"
            title={{ key: m.openFailedTitle }}
            body={{
              key: sessionMessages.openFailedReason,
              params: {
                code: persistErrorCode(error) ?? 'other',
                error: errorText(error, text.t(m.unknownError)),
              },
            }}
          />
        ) : (
          <div className={s.opening} role="status">
            <p>{text.t(m.opening)}</p>
            <ProgressBar value={null} label={{ key: m.opening }} />
          </div>
        )}
        <div className={s.crashActions}>
          {failed && (
            <Button
              variant="primary"
              icon="RotateCcw"
              onClick={() => {
                void services.nav.openProject(id as ProjectId).catch(() => undefined);
              }}
            >
              {text.t(m.retry)}
            </Button>
          )}
          <Button
            variant={failed ? 'secondary' : 'ghost'}
            icon="LibraryBig"
            onClick={() => {
              services.nav.goLibrary();
            }}
          >
            {text.t(m.backToLibrary)}
          </Button>
        </div>
      </div>
    );
  }

  const shown = isWorkspaceId(wsParam) ? wsParam : workspace;
  return (
    <DocStoreProvider store={store}>
      <div className={s.editor} aria-label={text.t(m.editorLabel)} data-workspace={shown}>
        <TopBar services={services} ui={ui} />
        <Banners services={services} ui={ui} />
        <WorkspaceLayout services={services} ui={ui} workspace={shown} />
        <HintBar services={services} ui={ui} />
      </div>
    </DocStoreProvider>
  );
}
